import { useRef, useState, useEffect } from "react";
import {
  IoCloseCircleOutline,
  IoInformationCircle,
} from "react-icons/io5";
import BreadCrumbs from "../../components/ui/BreadCrumbs";
import { Button } from "../../components/ui";
import { useApprovalsApi } from "../../hooks/useApprovalsApi";
import { useEmployeeTdsData } from "../../hooks/useEmployeeTdsData";
import { useProofOfInvestmentsApi } from "../employee_details/InvestmentsAndProofs/hooks/useProofOfInvestmentsApi";
import { useDataTransforms } from "../employee_details/InvestmentsAndProofs/hooks/useDataTransforms";
import PrimaryLoader from "../../components/common/PrimaryLoader";
import ImageModal from "../employee_details/InvestmentsAndProofs/components/ImageModal";
import CommentDrawer from "../employee_details/InvestmentsAndProofs/components/CommentDrawer";
import HouseRentSection from "./components/HouseRentSection";
import InvestmentApprovalSection from "./components/InvestmentApprovalSection";
import OtherIncomeSection from "./components/OtherIncomeSection";
import RentalIncomeSection from "./components/RentalIncomeSection";

const breadcrumbs = [
  { label: "Home", link: "/" },
  { label: "Approvals", link: "" },
];

const ApprovalsPage = () => {
  const {
    financialYearOptions,
    selectedFinancialYear,
    handleFinancialYearChange,
    employeeOptions,
    selectedEmployee,
    handleEmployeeSelection,
    isLoadingFinancialYears,
    isLoadingEmployees,
    getStatusDisplay,
  } = useApprovalsApi();

  const {
    employeeTdsData,
    isLoadingTdsData,
    loadEmployeeTdsData,
    handleApproval,
    handleRejection,
    handleMainApproval,
    isApprovingField,
    isRejectingField,
    isApprovingMainResponse,
    isAllItemsProcessed,
  } = useEmployeeTdsData();

  // Use the same hooks as ProofOfInvestments for attachments and comments
  const {
    attachments,
    setAttachments,
    activeAttachmentData,
    setActiveAttachmentData,
    openPopoverKey,
    setOpenPopoverKey,
    activeCommentData,
    setActiveCommentData,
    comments,
    setComments,
    commentText,
    setCommentText,
    isCommentInputFocused,
    setIsCommentInputFocused,
    currentProofListParams,
    setCurrentProofListParams,
    currentCommentListParams,
    setCurrentCommentListParams,
    commentPage,
    setCommentPage,
    hasMoreComments,
    setHasMoreComments,
    uploadDocument,
    deleteProof,
    addComment,
    proofList,
    setProofListParams,
    commentList,
    setCommentListParams,
  } = useProofOfInvestmentsApi();

  const { getCommentDrawerTitles } = useDataTransforms(employeeTdsData);

  // UI State
  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isCommentDrawerOpen, setIsCommentDrawerOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Load TDS data when employee is selected
  useEffect(() => {
    if (selectedEmployee) {
      loadEmployeeTdsData(
        selectedEmployee.employee_tds_submission_id,
        selectedEmployee.company_employee_id
      );
    }
  }, [selectedEmployee]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // File handling functions (same as ProofOfInvestments)
  const handleGenericFileSelect = (attachmentKey, idParams, documentCount) => {
    if (documentCount === 0) {
      setActiveAttachmentData({ attachmentKey, idParams });
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    } else {
      setOpenPopoverKey(attachmentKey);
      setCurrentProofListParams(idParams);
      setProofListParams(idParams);
    }
  };

  const handleAddMoreAttachments = (attachmentKey) => {
    setActiveAttachmentData({
      attachmentKey,
      idParams: currentProofListParams,
    });
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0 && activeAttachmentData) {
      const file = files[0];
      uploadDocument.handleAuth.mutate({
        "document[]": file,
        screen: "tds",
      });
    }
    event.target.value = "";
  };

  const handleRemoveAttachment = (key, index) => {
    const currentAttachments = attachments[key] || [];
    const proofToDelete = currentAttachments[index];
    if (proofToDelete?.id) {
      deleteProof.handleAuth.mutate({
        investment_proof_id: proofToDelete.id,
      });
    }
  };

  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    setIsImageModalOpen(true);
  };

  // Comment handling functions
  const handleGenericCommentClick = (commentKey, idParams) => {
    setActiveCommentData({ commentKey, idParams });
    setComments([]);
    setCommentPage(0);
    setCurrentCommentListParams({ ...idParams, limit: 10, page_number: 0 });
    setCommentListParams({ ...idParams, limit: 10, page_number: 0 });
    setIsCommentDrawerOpen(true);
  };

  const handleCommentDrawerClose = () => {
    setIsCommentDrawerOpen(false);
    setActiveCommentData(null);
    setComments([]);
    setCurrentCommentListParams(null);
    setCommentPage(0);
    setCommentText("");
    setIsCommentInputFocused(false);
  };

  const handleAddComment = (commentText) => {
    if (!activeCommentData || !commentText.trim()) return;
    const payload = {
      comment: commentText.trim(),
      ...activeCommentData.idParams,
    };
    addComment.handleAuth.mutate(payload);
  };

  const handleCommentTextChange = (text) => {
    setCommentText(text);
  };

  const handleCommentInputFocus = () => {
    setIsCommentInputFocused(true);
  };

  const handleCommentInputBlur = () => {
    if (!commentText.trim()) {
      setIsCommentInputFocused(false);
    }
  };

  const handleCancelComment = () => {
    setCommentText("");
    setIsCommentInputFocused(false);
  };

  const handleLoadMoreComments = () => {
    const nextPage = commentPage + 1;
    setCommentPage(nextPage);
    const newParams = {
      ...currentCommentListParams,
      page_number: nextPage,
    };
    setCurrentCommentListParams(newParams);
    setCommentListParams(newParams);
  };

  // Handle Consider for IT button click
  const handleConsiderForIT = () => {
    if (employeeTdsData?.main_submission_data?.employee_tds_submission_id) {
      handleMainApproval(employeeTdsData.main_submission_data.employee_tds_submission_id);
    }
  };

  // Check if all items are processed for the current employee
  const allItemsProcessed = isAllItemsProcessed(employeeTdsData);

  if (isLoadingFinancialYears) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <PrimaryLoader />
      </div>
    );
  }

  // Handle close button click
  const handleClose = () => {
    handleEmployeeSelection(null);
  };

  return (
    <div className="px-3 flex h-[calc(100vh-5rem)]">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.pdf,.doc,.docx"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Modals and Drawers */}
      <ImageModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        imageUrl={selectedImage}
        title="Proof Document"
      />

      <CommentDrawer
        isOpen={isCommentDrawerOpen}
        onClose={handleCommentDrawerClose}
        activeCommentKey={activeCommentData?.commentKey}
        title={
          activeCommentData
            ? getCommentDrawerTitles(activeCommentData.commentKey).title
            : "Comments"
        }
        subtitle={
          activeCommentData
            ? getCommentDrawerTitles(activeCommentData.commentKey).subtitle
            : "Investment Proof Comments"
        }
        comments={comments}
        onAddComment={handleAddComment}
        hasMoreComments={hasMoreComments}
        onLoadMoreComments={handleLoadMoreComments}
        isAddingComment={addComment.isLoading}
        isLoadingComments={commentList?.isLoading}
        commentText={commentText}
        onCommentTextChange={handleCommentTextChange}
        isCommentInputFocused={isCommentInputFocused}
        onCommentInputFocus={handleCommentInputFocus}
        onCommentInputBlur={handleCommentInputBlur}
        onCancelComment={handleCancelComment}
      />

      {/* Left Sidebar */}
      <div className="w-1/4 border-r p-4 shrink-0 self-stretch bg-white">
        <p className="text-xl font-semibold">Approval Pending Investments</p>
        <hr className="my-3" />
        
        {financialYearOptions.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">No financial years available</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <select 
                className="input text-sm col-span-2"
                value={selectedFinancialYear?.financial_year_id || ""}
                onChange={(e) => {
                  const selectedFY = financialYearOptions.find(
                    fy => fy.financial_year_id === e.target.value
                  );
                  if (selectedFY) {
                    handleFinancialYearChange(selectedFY);
                  }
                }}
              >
                {financialYearOptions.map(fy => (
                  <option key={fy.financial_year_id} value={fy.financial_year_id}>
                    {fy.label}
                  </option>
                ))}
              </select>
              <select 
                className="input text-sm col-span-2"
                value={selectedEmployee?.company_employee_id || ""}
                onChange={(e) => {
                  const selectedEmp = employeeOptions.find(
                    emp => emp.company_employee_id === e.target.value
                  );
                  if (selectedEmp) {
                    handleEmployeeSelection(selectedEmp);
                  }
                }}
                disabled={isLoadingEmployees || employeeOptions.length === 0}
              >
                <option value="" disabled>
                  {isLoadingEmployees
                    ? "Loading employees..."
                    : employeeOptions.length === 0
                    ? "No employees found"
                    : "Select employee"}
                </option>
                {employeeOptions.map(emp => (
                  <option key={emp.company_employee_id} value={emp.company_employee_id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>
            <hr className="my-3" />
            {isLoadingEmployees ? (
              <div className="flex justify-center py-4">
                <div className="text-sm text-gray-500">Loading employees...</div>
              </div>
            ) : employeeOptions.length > 0 ? (
              employeeOptions.map((employee) => {
                const statusDisplay = getStatusDisplay(employee.status);
                return (
                  <div 
                    key={employee.company_employee_id}
                    className={`p-4 border-y border-neutral-200 cursor-pointer hover:bg-gray-50 ${
                      selectedEmployee?.company_employee_id === employee.company_employee_id 
                        ? 'bg-blue-50 border-blue-200' 
                        : ''
                    }`}
                    onClick={() => handleEmployeeSelection(employee)}
                  >
                    <p className="font-medium">{employee.name}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-sm">{formatDate(employee.created_at)}</p>
                      <span className={`ml-2 ${statusDisplay.className}`}>
                        {statusDisplay.text}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">No employee submissions found</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Main Content Area */}
      <div className="overflow-auto w-3/4">
        <BreadCrumbs crumbs={breadcrumbs} className={"mx-5"} />

        {selectedEmployee ? (
          isLoadingTdsData ? (
            <div className="flex justify-center items-center h-full">
              <PrimaryLoader />
            </div>
          ) : employeeTdsData ? (
            <>
              <div className="py-3 px-5 border-b border-neutral-300 mb-5 flex items-center">
                <p className="text-xl font-semibold">{selectedEmployee.name}'s Investment Proofs</p>

                {/* Consider for IT button */}
                <div className="ml-auto">
                  <Button
                    variant="default"
                    className={`hover:opacity-75 ${
                      !allItemsProcessed || isApprovingMainResponse 
                        ? 'opacity-50 cursor-not-allowed hover:cursor-not-allowed' 
                        : ''
                    }`}
                    onClick={handleConsiderForIT}
                    disabled={!allItemsProcessed || isApprovingMainResponse}
                  >
                    {isApprovingMainResponse ? "Processing..." : "Consider for IT"}
                  </Button>
                </div>

                <button className="ml-3" onClick={handleClose}>
                  <IoCloseCircleOutline className="w-9 h-9 text-neutral-400 hover:text-neutral-600 transition-colors" />
                </button>
              </div>
              
              <div className="grid grid-cols-4 gap-5 mb-5 ml-5">
                <div>
                  <p className="mb-1.5 text-neutral-600">Submitted Date</p>
                  <p>{formatDate(selectedEmployee.created_at)}</p>
                </div>
                <div>
                  <p className="mb-1.5 text-neutral-600">Submitted by</p>
                  <p>{selectedEmployee.name}</p>
                </div>
                <div>
                  <p className="mb-1.5 text-neutral-600">Last modified by</p>
                  <p>--</p>
                </div>
                <div>
                  <p className="mb-1.5 text-neutral-600">Last modified date</p>
                  <p>--</p>
                </div>
              </div>

              <section className="border border-neutral-300 rounded-lg ml-5">
                <div className="flex items-center p-3 bg-blue-50 w-full">
                  <IoInformationCircle className="w-6 h-6 text-blue-600 mr-2" />
                  <p className="font-medium">
                    Tax Regime:
                    <span className="font-medium"> 
                      {employeeTdsData.main_submission_data?.employee_regime_selection?.regime === 'old' 
                        ? 'Old Tax Regime' 
                        : 'New Tax Regime'}
                    </span>
                  </p>
                </div>
                
                {/* Table Header */}
                <div className="flex items-center mx-3 my-3">
                  <p className="flex-no-grow-no-shrink px-1.5 w-[28.4%] text-center">
                    Declaration type
                  </p>
                  <p className="flex-no-grow-no-shrink px-1.5 w-[14.2%] text-center">
                    Actual amount
                  </p>
                  <p className="flex-no-grow-no-shrink px-1.5 w-[14.2%] text-center">
                    Proof
                  </p>
                  <p className="flex-no-grow-no-shrink px-1.5 w-[14.2%] text-center">
                    Comments
                  </p>
                  <p className="flex-no-grow-no-shrink px-1.5 w-[14.2%] text-center">
                    Approved amount
                  </p>
                  <p className="flex-no-grow-no-shrink px-1.5 w-[14.2%] text-center">
                    Actions
                  </p>
                </div>
                <hr className="mb-5 mx-3" />
                
                {/* House Rent Section - Only for Old Tax Regime */}
                {employeeTdsData.main_submission_data?.employee_regime_selection?.regime === 'old' && (
                  <HouseRentSection
                    houseRentData={employeeTdsData.house_rent}
                    attachments={attachments}
                    onFileSelect={handleGenericFileSelect}
                    onAddMoreAttachments={handleAddMoreAttachments}
                    onRemoveAttachment={handleRemoveAttachment}
                    onOpenImage={openImageModal}
                    openPopoverKey={openPopoverKey}
                    setOpenPopoverKey={setOpenPopoverKey}
                    onCommentClick={handleGenericCommentClick}
                    onApproval={handleApproval}
                    onRejection={handleRejection}
                    isApproving={isApprovingField}
                    isRejecting={isRejectingField}
                  />
                )}

                {/* Other Income Section - Show for both regimes */}
                <OtherIncomeSection
                  otherSourceIncome={employeeTdsData.other_source_income}
                  attachments={attachments}
                  onFileSelect={handleGenericFileSelect}
                  onAddMoreAttachments={handleAddMoreAttachments}
                  onRemoveAttachment={handleRemoveAttachment}
                  onOpenImage={openImageModal}
                  openPopoverKey={openPopoverKey}
                  setOpenPopoverKey={setOpenPopoverKey}
                  onCommentClick={handleGenericCommentClick}
                  onApproval={handleApproval}
                  onRejection={handleRejection}
                  isApproving={isApprovingField}
                  isRejecting={isRejectingField}
                />

                {/* Rental Income Section */}
                <RentalIncomeSection
                  rentalIncomeData={employeeTdsData.rental_income}
                  homeLoanData={employeeTdsData.home_loan}
                  onApproval={handleApproval}
                  onRejection={handleRejection}
                  isApproving={isApprovingField}
                  isRejecting={isRejectingField}
                  taxRegime={employeeTdsData.main_submission_data?.employee_regime_selection?.regime}
                  // Add proof and comment handlers
                  attachments={attachments}
                  onFileSelect={handleGenericFileSelect}
                  onAddMoreAttachments={handleAddMoreAttachments}
                  onRemoveAttachment={handleRemoveAttachment}
                  onOpenImage={openImageModal}
                  openPopoverKey={openPopoverKey}
                  setOpenPopoverKey={setOpenPopoverKey}
                  onCommentClick={handleGenericCommentClick}
                />

                {/* 80C Investments - Only for Old Tax Regime */}
                {employeeTdsData.main_submission_data?.employee_regime_selection?.regime === 'old' && (
                  <InvestmentApprovalSection
                    title="80C Investments"
                    investments={employeeTdsData.investments_80C}
                    attachments={attachments}
                    onFileSelect={handleGenericFileSelect}
                    onAddMoreAttachments={handleAddMoreAttachments}
                    onRemoveAttachment={handleRemoveAttachment}
                    onOpenImage={openImageModal}
                    openPopoverKey={openPopoverKey}
                    setOpenPopoverKey={setOpenPopoverKey}
                    onCommentClick={handleGenericCommentClick}
                    onApproval={handleApproval}
                    onRejection={handleRejection}
                    isApproving={isApprovingField}
                    isRejecting={isRejectingField}
                    keyPrefix="80c"
                    getIdParams={(investment) => ({ emp_80c_id: investment.emp_80c_id })}
                  />
                )}

                {/* 80D Exemptions - Only for Old Tax Regime */}
                {employeeTdsData.main_submission_data?.employee_regime_selection?.regime === 'old' && (
                  <InvestmentApprovalSection
                    title="80D Exemptions"
                    investments={employeeTdsData.exemptions_80D}
                    attachments={attachments}
                    onFileSelect={handleGenericFileSelect}
                    onAddMoreAttachments={handleAddMoreAttachments}
                    onRemoveAttachment={handleRemoveAttachment}
                    onOpenImage={openImageModal}
                    openPopoverKey={openPopoverKey}
                    setOpenPopoverKey={setOpenPopoverKey}
                    onCommentClick={handleGenericCommentClick}
                    onApproval={handleApproval}
                    onRejection={handleRejection}
                    isApproving={isApprovingField}
                    isRejecting={isRejectingField}
                    keyPrefix="80d"
                    getIdParams={(exemption) => ({ emp_80d_id: exemption.emp_80d_id })}
                  />
                )}

                {/* Other Investments - Only for Old Tax Regime */}
                {employeeTdsData.main_submission_data?.employee_regime_selection?.regime === 'old' && (
                  <InvestmentApprovalSection
                    title="Other Investments & Exemptions"
                    investments={employeeTdsData.other_investments}
                    attachments={attachments}
                    onFileSelect={handleGenericFileSelect}
                    onAddMoreAttachments={handleAddMoreAttachments}
                    onRemoveAttachment={handleRemoveAttachment}
                    onOpenImage={openImageModal}
                    openPopoverKey={openPopoverKey}
                    setOpenPopoverKey={setOpenPopoverKey}
                    onCommentClick={handleGenericCommentClick}
                    onApproval={handleApproval}
                    onRejection={handleRejection}
                    isApproving={isApprovingField}
                    isRejecting={isRejectingField}
                    keyPrefix="other"
                    getIdParams={(investment) => ({ 
                      emp_other_investment_id: investment.emp_other_investment_id 
                    })}
                  />
                )}
              </section>
              <div className="py-5"></div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="text-lg text-gray-500 mb-2">No Data Available</p>
                <p className="text-sm text-gray-400">Unable to load employee investment data</p>
              </div>
            </div>
          )
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-lg text-gray-500 mb-2">No Employee Selected</p>
              <p className="text-sm text-gray-400">Select an employee from the left sidebar to view their investment details</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalsPage;
